/**
 * ProductionRecords.tsx
 * Supervisor/Manager page to view all production records (ICS legacy + HTE staging).
 * Row click → opens the detail page. The chevron toggles inline expansion for a quick
 * peek at input/output detail rows without leaving the list.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import {
  productionRecordService,
  ProductionRecord,
  ProductionRecordDetail,
} from '../../services/productionRecordService';
import toast from 'react-hot-toast';
import {
  Database,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  ExternalLink,
  ArrowRight,
  Calendar,
} from 'lucide-react';
import DatePickerDropdown from '../../components/DatePickerDropdown';
import SessionDetailsModal from './components/SessionDetailsModal';

// Format YYYYMMDD date string to readable format
const formatDate = (dateStr: string | null): string => {
  if (!dateStr || dateStr.length < 8) return '-';
  return `${dateStr.slice(4, 6)}/${dateStr.slice(6, 8)}/${dateStr.slice(0, 4)}`;
};

// A display item is either a single ICS record or a paired HTE group.
// WCO-2417/2418: a production day's single INPUT can fan out to MULTIPLE OUTPUT records (one per
// destination), so outputRecords is a list, not a single record.
interface HtePair {
  type: 'hte';
  key: string;
  inputRecord: ProductionRecord;
  outputRecords: ProductionRecord[];
  sessionNumber: string | null;
}

interface IcsItem {
  type: 'ics';
  key: string;
  record: ProductionRecord;
}

type DisplayItem = HtePair | IcsItem;

const ProductionRecords: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  usePageTitle(embedded ? '' : t('nav.productionRecords'));

  // Data state
  const [records, setRecords] = useState<ProductionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Session details modal
  const [selectedSessionNumber, setSelectedSessionNumber] = useState<string | null>(null);

  // Inline expansion (chevron) — quick peek without leaving the list
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [expandedInputDetails, setExpandedInputDetails] = useState<ProductionRecordDetail[]>([]);
  // Per-output details keyed by output production_number (one entry per destination). WCO-2417.
  const [expandedOutputs, setExpandedOutputs] = useState<Array<{ record: ProductionRecord; details: ProductionRecordDetail[] }>>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Row click → detail page. For HTE pairs prefer the Input record's number; the detail
  // page fetches the -O sibling and surfaces both halves in tabs.
  const openRecord = (productionNumber: string) => {
    navigate(`/production-records/${encodeURIComponent(productionNumber)}`);
  };

  // Toggle inline expansion for an HTE pair. Fetches the input + EVERY output (one per destination)
  // in parallel, keeping each output distinct so the expansion shows them grouped by destination. WCO-2417.
  const toggleExpandHte = async (pair: HtePair) => {
    if (expandedKey === pair.key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(pair.key);
    setLoadingDetails(true);
    setExpandedInputDetails([]);
    setExpandedOutputs([]);
    try {
      const fetches: Promise<void>[] = [];
      if (pair.inputRecord) {
        fetches.push(
          productionRecordService.getRecordDetails(pair.inputRecord.production_number)
            .then(data => setExpandedInputDetails(data.details))
            .catch(() => setExpandedInputDetails([])),
        );
      }
      const outputResults = await Promise.all(
        pair.outputRecords.map(rec =>
          productionRecordService.getRecordDetails(rec.production_number)
            .then(data => ({ record: rec, details: data.details }))
            .catch(() => ({ record: rec, details: [] as ProductionRecordDetail[] })),
        ),
      );
      setExpandedOutputs(outputResults);
      await Promise.all(fetches);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Toggle inline expansion for a standalone ICS record. Splits details by p_type.
  const toggleExpandIcs = async (record: ProductionRecord) => {
    if (expandedKey === record.production_number) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(record.production_number);
    setLoadingDetails(true);
    setExpandedInputDetails([]);
    setExpandedOutputs([]);
    try {
      const data = await productionRecordService.getRecordDetails(record.production_number);
      setExpandedInputDetails(data.details.filter(d => d.p_type === 'I'));
      const outputRows = data.details.filter(d => d.p_type === 'O');
      setExpandedOutputs(outputRows.length > 0 ? [{ record, details: outputRows }] : []);
    } catch (err) {
      toast.error('Failed to load record details');
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [page, sourceFilter]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: 50 };
      if (sourceFilter) params.source = sourceFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (dateFrom) params.date_from = dateFrom.replace(/-/g, '');
      if (dateTo) params.date_to = dateTo.replace(/-/g, '');

      const data = await productionRecordService.getRecords(params);
      setRecords(data.records);
      setTotalPages(data.pagination.total_pages);
      setTotal(data.pagination.total);
    } catch (err) {
      toast.error('Failed to load production records');
    } finally {
      setLoading(false);
    }
  };

  // Group records into I/O pairs using reference_numbers or -I/-O suffixes.
  // An output record's reference_numbers points to its input record.
  const displayItems: DisplayItem[] = useMemo(() => {
    const byProdNum = new Map<string, ProductionRecord>();
    for (const r of records) byProdNum.set(r.production_number, r);

    const paired = new Set<string>(); // production numbers already grouped
    const result: DisplayItem[] = [];

    for (const r of records) {
      if (paired.has(r.production_number)) continue;

      // Helper: all not-yet-paired output records that reference `inputNum` (multi-destination),
      // ordered by production number ascending so destinations show incrementally
      // (e.g. 900521 before 900522). WCO-2417.
      const outputsFor = (inputNum: string): ProductionRecord[] =>
        records
          .filter(o => !paired.has(o.production_number) && o.dominant_p_type === 'O' && o.reference_numbers?.includes(inputNum))
          .sort((a, b) => {
            const ka = Number(a.production_number);
            const kb = Number(b.production_number);
            if (Number.isFinite(ka) && Number.isFinite(kb) && ka !== kb) return ka - kb;
            return a.production_number.localeCompare(b.production_number);
          });

      // Strategy 1: -I/-O suffix pairing (inherently one input ↔ one output)
      const hasIOSuffix = r.production_number.endsWith('-I') || r.production_number.endsWith('-O');
      if (hasIOSuffix) {
        const baseNumber = r.production_number.slice(0, -2);
        const siblingSuffix = r.production_number.endsWith('-I') ? '-O' : '-I';
        const sibling = byProdNum.get(baseNumber + siblingSuffix);

        const inputRec = r.production_number.endsWith('-I') ? r : sibling;
        const outputRec = r.production_number.endsWith('-O') ? r : sibling;

        if (inputRec || outputRec) {
          paired.add(r.production_number);
          if (sibling) paired.add(sibling.production_number);
          const primary = inputRec || outputRec!;
          result.push({
            type: 'hte',
            key: `pair-${baseNumber}`,
            inputRecord: inputRec || primary,
            outputRecords: outputRec ? [outputRec] : [],
            sessionNumber: primary.session_number,
          });
          continue;
        }
      }

      // Strategy 2: reference_numbers pairing (output references its input). Collect ALL outputs
      // that reference the same input, so a multi-destination day is one grouped row.
      if (r.dominant_p_type === 'O' && r.reference_numbers?.length) {
        const inputNum = r.reference_numbers[0];
        const inputRec = byProdNum.get(inputNum);
        if (inputRec && !paired.has(inputNum)) {
          const outputRecs = outputsFor(inputNum); // includes r
          paired.add(inputNum);
          for (const o of outputRecs) paired.add(o.production_number);
          result.push({
            type: 'hte',
            key: `pair-${inputNum}`,
            inputRecord: inputRec,
            outputRecords: outputRecs,
            sessionNumber: inputRec.session_number || r.session_number,
          });
          continue;
        }
      }

      // Strategy 2b: input record — collect ALL outputs that reference us
      if (r.dominant_p_type === 'I') {
        const outputRecs = outputsFor(r.production_number);
        if (outputRecs.length > 0) {
          paired.add(r.production_number);
          for (const o of outputRecs) paired.add(o.production_number);
          result.push({
            type: 'hte',
            key: `pair-${r.production_number}`,
            inputRecord: r,
            outputRecords: outputRecs,
            sessionNumber: r.session_number || outputRecs[0].session_number,
          });
          continue;
        }
      }

      // No pair found — standalone record
      result.push({ type: 'ics', key: r.production_number, record: r });
    }

    return result;
  }, [records]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  // Source badge class
  const sourceCls = (source: string, prodNum: string) => {
    const baseNum = parseInt(prodNum.replace(/-[IO]$/, ''));
    const isHte = source === 'HTE' || baseNum >= 900001;
    return isHte
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  };
  const sourceLabel = (source: string, prodNum: string) => {
    const baseNum = parseInt(prodNum.replace(/-[IO]$/, ''));
    return (source === 'HTE' || baseNum >= 900001) ? 'HTE' : 'ICS';
  };


  const thCls = 'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';
  const tdCls = 'px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap';

  return (
    <div className={embedded ? 'space-y-4' : 'p-4 md:p-6'}>
      {/* Filters + refresh */}
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('productionRecords.source')}</label>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">{t('productionRecords.allSources')}</option>
            <option value="ICS">ICS</option>
            <option value="HTE">HTE</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
          <DatePickerDropdown selected={dateFrom || null} onSelect={(date) => setDateFrom(date)}>
            {({ onClick }) => (
              <button type="button" onClick={onClick} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <Calendar className="w-4 h-4 text-gray-400" />
                {dateFrom ? formatDate(dateFrom.replace(/-/g, '')) : 'Select date...'}
              </button>
            )}
          </DatePickerDropdown>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
          <DatePickerDropdown selected={dateTo || null} onSelect={(date) => setDateTo(date)}>
            {({ onClick }) => (
              <button type="button" onClick={onClick} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <Calendar className="w-4 h-4 text-gray-400" />
                {dateTo ? formatDate(dateTo.replace(/-/g, '')) : 'Select date...'}
              </button>
            )}
          </DatePickerDropdown>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.search')}</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={`${t('productionRecords.productionNumber')}, memo...`}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <button onClick={handleSearch} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {!loading && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
          )}
          <button
            onClick={() => { setPage(1); loadRecords(); }}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : displayItems.length === 0 ? (
        <div className="text-center py-12">
          <Database className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('productionRecords.noRecords')}</p>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className={`${thCls} w-8`} aria-label="expand" />
                  <th className={thCls}>{t('productionRecords.productionNumber')}</th>
                  <th className={thCls}>{t('productionRecords.source')}</th>
                  <th className={thCls}>{t('productionRecords.dateRange')}</th>
                  <th className={thCls}>{t('productionRecords.location')}</th>
                  <th className={thCls}>{t('productionRecords.operator')}</th>
                  <th className={thCls}>{t('productionRecords.detailCount')}</th>
                  <th className={thCls}>{t('productionRecords.createdBy')}</th>
                  <th className={`${thCls} w-8`} aria-label="actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {displayItems.map((item) => {
                  const isExpanded = expandedKey === item.key;
                  const isLoadingThis = loadingDetails && isExpanded;

                  if (item.type === 'hte') {
                    const primary = item.inputRecord;
                    // Row click → detail page (Input record; the page fetches the -O sibling).
                    // Chevron click → inline expand for a quick peek.
                    return (
                      <React.Fragment key={item.key}>
                        <tr
                          onClick={() => openRecord(primary.production_number)}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <td
                            className="px-3 py-2 text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                            onClick={(e) => { e.stopPropagation(); toggleExpandHte(item); }}
                            title={isExpanded ? 'Collapse' : 'Expand details'}
                          >
                            {isExpanded
                              ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 inline" />
                              : <ChevronDown className="w-3.5 h-3.5 text-gray-400 inline" />}
                          </td>
                          <td className={tdCls}>
                            <span className="font-mono text-blue-600 dark:text-blue-400">{primary.production_number}</span>
                            <ArrowRight className="w-3 h-3 text-gray-400 inline mx-1" />
                            {/* Multi-destination: list every output number, comma-separated. WCO-2417. */}
                            <span className="font-mono text-emerald-600 dark:text-emerald-400">
                              {item.outputRecords.length > 0
                                ? item.outputRecords.map(o => o.production_number).join(', ')
                                : '-'}
                            </span>
                          </td>
                          <td className={tdCls}>
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${sourceCls(primary.source, primary.production_number)}`}>
                              {sourceLabel(primary.source, primary.production_number)}
                            </span>
                          </td>
                          <td className={tdCls}>{formatDate(primary.starting_date)}</td>
                          <td className={tdCls}>{primary.location_code || '-'}</td>
                          <td className={tdCls}>{primary.operator_id || '-'}</td>
                          <td className={`${tdCls} text-center`}>{primary.detail_count}</td>
                          <td className={tdCls}>{primary.created_by_name || '-'}</td>
                          <td className={tdCls}>
                            {item.sessionNumber && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedSessionNumber(item.sessionNumber); }}
                                className="text-blue-500 hover:text-blue-700 dark:text-blue-400"
                                title={`Session ${item.sessionNumber}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className="p-0">
                              <ExpandedDetails
                                loading={isLoadingThis}
                                inputDetails={expandedInputDetails}
                                outputGroups={expandedOutputs}
                                onOpenRecord={openRecord}
                                t={t}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  // ICS standalone record
                  const rec = item.record;
                  const isOutput = rec.dominant_p_type === 'O';
                  return (
                    <React.Fragment key={item.key}>
                      <tr
                        onClick={() => openRecord(rec.production_number)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td
                          className="px-3 py-2 text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                          onClick={(e) => { e.stopPropagation(); toggleExpandIcs(rec); }}
                          title={isExpanded ? 'Collapse' : 'Expand details'}
                        >
                          {isExpanded
                            ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 inline" />
                            : <ChevronDown className="w-3.5 h-3.5 text-gray-400 inline" />}
                        </td>
                        <td className={tdCls}>
                          <span className="font-mono">{rec.production_number}</span>
                          <span className={`ml-1.5 px-1.5 py-0.5 text-xs font-medium rounded ${isOutput ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                            {isOutput ? 'O' : 'I'}
                          </span>
                        </td>
                        <td className={tdCls}>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${sourceCls(rec.source, rec.production_number)}`}>
                            {sourceLabel(rec.source, rec.production_number)}
                          </span>
                        </td>
                        <td className={tdCls}>{formatDate(rec.starting_date)}</td>
                        <td className={tdCls}>{rec.location_code || '-'}</td>
                        <td className={tdCls}>{rec.operator_id || '-'}</td>
                        <td className={`${tdCls} text-center`}>{rec.detail_count}</td>
                        <td className={tdCls}>{rec.created_by_name || '-'}</td>
                        <td className={tdCls} />
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="p-0">
                            <ExpandedDetails
                              loading={isLoadingThis}
                              inputDetails={expandedInputDetails}
                              outputGroups={expandedOutputs}
                              t={t}
                              relatedRecords={rec.reference_numbers}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-3">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Session Details Modal */}
      {selectedSessionNumber && (
        <SessionDetailsModal
          session={{
            session_number: selectedSessionNumber,
            product_sku: '',
            product_name: '',
            status: '',
          }}
          onClose={() => setSelectedSessionNumber(null)}
          initialTab="details"
        />
      )}
    </div>
  );
};

// ============================================================================
// Expanded detail sub-row — two-column mini-tables for input & output
// ============================================================================
const ExpandedDetails: React.FC<{
  loading: boolean;
  inputDetails: ProductionRecordDetail[];
  // WCO-2417: one entry per output destination (record + its detail rows), so a multi-destination
  // day shows a separate labeled Output column per destination instead of one merged column.
  outputGroups: Array<{ record: ProductionRecord; details: ProductionRecordDetail[] }>;
  t: (key: string) => string;
  relatedRecords?: string[] | null;
  onOpenRecord?: (productionNumber: string) => void;
}> = ({ loading, inputDetails, outputGroups, t, relatedRecords, onOpenRecord }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700/50">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasOutputs = outputGroups.some(g => g.details.length > 0);
  if (inputDetails.length === 0 && !hasOutputs) {
    return (
      <div className="py-3 text-center text-xs text-gray-400 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700/50">
        No detail lines
      </div>
    );
  }

  // Column count: input column (if any) + one column per non-empty output group, capped at 3 for layout.
  const outputColCount = outputGroups.filter(g => g.details.length > 0).length;
  const totalCols = (inputDetails.length > 0 ? 1 : 0) + outputColCount;
  const gridCols = totalCols >= 3 ? 'md:grid-cols-3' : totalCols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

  return (
    <div className="bg-gray-50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700/50 px-4 py-3">
      <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
        {inputDetails.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1.5 uppercase tracking-wider">
              {t('productionRecords.inputMaterials')} ({inputDetails.length})
            </h4>
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-400 dark:text-gray-500 uppercase">
                  <th className="text-left py-1 pr-2">Item</th>
                  <th className="text-right py-1 px-2 w-16">Qty</th>
                  <th className="text-right py-1 pl-2 w-24">Weight</th>
                </tr>
              </thead>
              <tbody>
                {inputDetails.map((d) => (
                  <DetailTableRow key={d.id} detail={d} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {outputGroups.filter(g => g.details.length > 0).map((group) => {
          // Per-destination heading: "Output Products · <location> · <production_number>".
          // The production number is clickable — jumps to that destination's own detail page.
          const loc = group.record.location_code;
          return (
            <div key={group.record.production_number}>
              <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1.5 uppercase tracking-wider flex items-center gap-1.5">
                <span>{t('productionRecords.outputProducts')} ({group.details.length})</span>
                {loc && <span className="text-gray-400 normal-case font-normal">· {loc}</span>}
                {onOpenRecord && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenRecord(group.record.production_number); }}
                    className="font-mono normal-case font-normal text-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300 inline-flex items-center gap-0.5"
                    title={`Open ${group.record.production_number}`}
                  >
                    {group.record.production_number}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </h4>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-400 dark:text-gray-500 uppercase">
                    <th className="text-left py-1 pr-2">Item</th>
                    <th className="text-right py-1 px-2 w-16">Qty</th>
                    <th className="text-right py-1 pl-2 w-24">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {group.details.map((d) => (
                    <DetailTableRow key={d.id} detail={d} />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {relatedRecords && relatedRecords.length > 0 && (
        <p className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-400">
          Related: {relatedRecords.join(', ')}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// DetailTableRow — single compact row in the detail mini-table
// ============================================================================
const DetailTableRow: React.FC<{ detail: ProductionRecordDetail }> = ({ detail }) => {
  const [showWeights, setShowWeights] = useState(false);
  const weightCount = parseInt(String(detail.weight_count)) || 0;
  const totalWeight = parseFloat(String(detail.total_weight)) || 0;
  const qty = parseFloat(String(detail.quantity)) || 0;
  const qtyShipped = parseFloat(String(detail.quantity_shipped)) || 0;
  const weights = detail.weights || [];

  const isOutput = detail.p_type === 'O';
  const displayQty = isOutput ? (weightCount || qtyShipped || qty) : (qtyShipped || qty);
  const hasWeights = weightCount > 0;
  const allSameWeight = weights.length > 1 && weights.every(w => w.weight === weights[0].weight);

  return (
    <>
      <tr
        onClick={() => hasWeights && setShowWeights(!showWeights)}
        className={`text-sm border-t border-gray-100 dark:border-gray-700/30 ${hasWeights ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/50' : ''}`}
      >
        <td className="py-1.5 pr-2 text-gray-800 dark:text-gray-200 truncate max-w-[280px]" title={`${detail.item_english_name || ''} (${detail.item_number})`}>
          {detail.item_english_name || detail.item_number}
          {detail.item_english_name && (
            <span className="ml-1.5 text-xs text-gray-400">{detail.item_number}</span>
          )}
        </td>
        <td className="py-1.5 px-2 text-right font-mono text-gray-600 dark:text-gray-400">
          {displayQty > 0 ? displayQty : '-'}
        </td>
        <td className="py-1.5 pl-2 text-right font-mono text-gray-600 dark:text-gray-400">
          {totalWeight > 0 ? `${totalWeight.toFixed(2)}` : '-'}
          {hasWeights && (
            <span className="ml-1 text-blue-500 text-xs">
              ({weightCount}w)
            </span>
          )}
        </td>
      </tr>
      {showWeights && weights.length > 0 && (
        <tr>
          <td colSpan={3} className="py-1.5 pl-4">
            {allSameWeight ? (
              <span className="text-xs font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5">
                {parseFloat(weights[0].weight).toFixed(2)} lbs x {weights.length}
              </span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {weights.map((w, idx) => (
                  <span key={w.id} className="text-xs font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5">
                    #{idx + 1}: {parseFloat(w.weight).toFixed(2)}
                  </span>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export default ProductionRecords;
